import React from 'react';
import { observer } from 'mobx-react';

import { RecordingAsset } from '@kaltura-ott/tvpil-shared';
import { Bookmark } from '@kaltura-ott/tvpil-shared/lib/rhino/data/asset';

import { Card, CardBubbleInfo, CardInner, CardTitle } from 'components/presenters';

import { RecordingCardContent } from './components';
import { useRecordingCard } from './hooks';

interface Props {
  bookmark?: Bookmark;
  recording: RecordingAsset;
  imageWidth: number;
  imageHeight: number;
}

function RecordingCard({ recording, imageWidth, imageHeight, bookmark }: Props) {
  const {
    link,
    title,
    rating,
    isRecord,
    ariaLabel,
    handleRef,
    isPlayable,
    channelName,
    handleClick,
    isStandalone,
    handleLinkRef,
    isSeriesRecord,
    handleInfoClick,
    isDisplayRating,
    defaultImageUrl,
    isRecordOngoing,
    watchingProgress,
    airingDateObject,
    handlerMouseEnter,
    handlerMouseLeave,
    backgroundImageUrl,
    seasonEpisodeTitle,
    newEpisodeBadgeMessage,
    episodesCounterMessage,
    expirationBadgeMessage,
  } = useRecordingCard({ recording, bookmark, height: imageHeight, width: imageWidth });

  return (
    <Card
      handleLinkRef={handleLinkRef}
      clickHandler={handleClick}
      onMouseEnter={handlerMouseEnter}
      onMouseLeave={handlerMouseLeave}
      link={link}
      imageHeight={imageHeight}
      itemWidth={imageWidth}
      isMosaicProgram={false}
      ariaLabel={ariaLabel}
    >
      <CardInner
        cardType='recording'
        handleLinkRef={handleLinkRef}
        handleRef={handleRef}
        defaultImageUrl={defaultImageUrl}
        isNoDataWithNoLogoImg={false}
        name={title}
        url={backgroundImageUrl}
        contentOverlay={
          <RecordingCardContent
            airingDateObject={airingDateObject}
            isRecordOngoing={isRecordOngoing}
            expirationBadgeMessage={expirationBadgeMessage}
            newEpisodeBadgeMessage={newEpisodeBadgeMessage}
            episodesCounterMessage={episodesCounterMessage}
            progress={watchingProgress}
            rating={rating}
            shouldShowPlayButton={isStandalone && isPlayable}
            shouldShowRating={isDisplayRating}
            shouldShowAiringTime={isStandalone && !isPlayable}
          />
        }
      />
      <CardBubbleInfo
        isRecord={isRecord}
        isSeriesRecord={isSeriesRecord}
        airingDate={airingDateObject}
        shouldShowAiringTime={isStandalone}
        channelHasProgram
        channelName={channelName}
        handleClickInfo={handleInfoClick}
        isLinkAsAsset={false}
        isMosaicProgram={false}
        seasonEpisodeTitle={isStandalone ? seasonEpisodeTitle : undefined}
        title={title}
      />
      <CardTitle
        isRecord={isRecord}
        isSeriesRecord={isSeriesRecord}
        isMosaicProgram={false}
        isStaticInfo
        seasonEpisodeTitle={isStandalone ? seasonEpisodeTitle : undefined}
        title={title}
      />
    </Card>
  );
}

export default observer(RecordingCard);
